import PropTypes from 'prop-types'
import React from 'react'

import { GOODS_TYPES } from 'constants/sales'
import useInViewCommon from 'hooks/useInViewCommon'
import { H2 } from 'styles/components/typography'

import { calcDnaBarWidth, calcDiff } from '../utils'

import { diffsTranslate, timeline, barsChangeState } from './animation'
import Indicator from './components/Indicator'
import {
  Wrapper,
  DifferenceBox,
  Diff,
  DifferenceInner,
  IndicatorsBox,
  Inner,
  ScenesDuration,
  ScenesDurationsWrapper,
} from './styles'
import useScrollMagic from 'hooks/useScrollMagic'

const DOWN = 'FORWARD'

const WAboutCompareDetailsDesktop = ({ data }) => {
  const wrapper = React.useRef()
  const wgsIndicatorRef = React.useRef()
  const dnaIndicatorRef = React.useRef()
  const diffsRef = React.useRef()
  const [refs] = React.useState(() => data.map(() => React.createRef()))
  const [scenesBlocksRef] = React.useState(() =>
    data.map(() => React.createRef()),
  )

  const [indicators, changeIndicators] = React.useState(() => ({ ...data[0] }))
  const { dna, wgs } = GOODS_TYPES
  const [ref, inView] = useInViewCommon()

  const { ScrollMagic, isScrollMagicLoaded } = useScrollMagic({ inView })

  React.useEffect(() => {
    if (inView) {
      require('animation.gsap')
    }
  }, [inView])

  React.useEffect(() => {
    if (inView && isScrollMagicLoaded) {
      const heights = refs.map(
        item => item.current.getBoundingClientRect().height,
      )
      const offsets = heights.map((item, index, arr) => {
        if (index !== arr.length - 1) {
          return heights.slice(0, index + 1).reduce((a, b) => a + b)
        }

        return heights.slice(0, index).reduce((a, b) => a + b)
      })
      const sceneSettings = {
        triggerElement: wrapper.current,
        triggerHook: 0.4,
        duration: `${100 * (data.length - 1)}%`,
      }
      const {
        current: {
          textRef: { current: wgsText },
          barRef: { current: wgsBar },
          imageRef: { current: wgsBox },
        },
      } = wgsIndicatorRef
      const {
        current: {
          textRef: { current: dnaText },
          barRef: { current: dnaBar },
          imageRef: { current: dnaBox },
        },
      } = dnaIndicatorRef
      const chartsShowAnim = timeline({
        wgsText,
        wgsBar,
        wgsBox,
        dnaText,
        dnaBar,
        dnaBox,
      })

      const controller = new ScrollMagic.Controller()
      const scene = new ScrollMagic.Scene(sceneSettings).setPin(wrapper.current)
      const diffsScene = new ScrollMagic.Scene(sceneSettings).setPin(
        diffsRef.current,
      )

      const scenesArr = data.map((item, index) =>
        new ScrollMagic.Scene({
          triggerHook: 0.4,
          triggerElement: scenesBlocksRef[index].current,
          duration: '100%',
        })
          .on('enter', ev => {
            changeIndicators({ ...data[index], index })
            if (index === 0 && ev.scrollDirection === DOWN) {
              chartsShowAnim.play()
            } else {
              barsChangeState({
                wgsBar,
                dnaBar,
                wgsText,
                dnaText,
                data: data[index],
              })
            }
          })
          .setTween(diffsTranslate({ offset: offsets[index], diffsRef })),
      )

      const diffsFadeInScenes = data.map((item, index) =>
        new ScrollMagic.Scene({
          triggerElement: scenesBlocksRef[index].current,
          triggerHook: 0.4,
          duration: '100%',
        }).setClassToggle(refs[index].current, 'active'),
      )

      controller.addScene([scene, diffsScene, scenesArr, diffsFadeInScenes])

      return () => {
        controller.destroy()
        chartsShowAnim.kill()
      }
    }
  }, [data, inView, refs, scenesBlocksRef, isScrollMagicLoaded])

  const baseTriggerElementsForScenes = React.useMemo(() => {
    if (inView) {
      return (
        <ScenesDurationsWrapper>
          {data.map((item, index) => (
            <ScenesDuration key={index} ref={scenesBlocksRef[index]} />
          ))}
        </ScenesDurationsWrapper>
      )
    }

    return null
  }, [inView, data, scenesBlocksRef])

  return (
    <Wrapper ref={ref}>
      <Inner>
        <div ref={diffsRef}>
          {data.map((item, index) => (
            <DifferenceBox key={index} ref={refs[index]}>
              <DifferenceInner>
                <Diff
                  dangerouslySetInnerHTML={{
                    __html: `+${calcDiff(
                      item.wgs_indicators,
                      item.dna_indicators,
                    )}`,
                  }}
                />
                <H2 as="span" dangerouslySetInnerHTML={{ __html: item.text }} />
              </DifferenceInner>
            </DifferenceBox>
          ))}
        </div>
      </Inner>

      <div style={{ width: '63%' }}>
        <div ref={wrapper}>
          <IndicatorsBox>
            <Indicator
              index={indicators.index}
              type={wgs}
              text={indicators.wgs_indicators}
              barWidth={100}
              ref={wgsIndicatorRef}
            />

            <Indicator
              index={indicators.index}
              type={dna}
              text={indicators.dna_indicators}
              barWidth={calcDnaBarWidth(
                indicators.wgs_indicators,
                indicators.dna_indicators,
              )}
              ref={dnaIndicatorRef}
            />
          </IndicatorsBox>
        </div>
      </div>

      {baseTriggerElementsForScenes}
    </Wrapper>
  )
}

WAboutCompareDetailsDesktop.propTypes = {
  data: PropTypes.array.isRequired,
}

export default WAboutCompareDetailsDesktop
